import { z } from 'zod';
import { defaulted, mediaRefSchema, translatableString } from '../common.js';
import { blockCommonSchema, blockCtaSchema } from './base.js';
import { EMBEDDED_LIMITS } from '../../constants/index.js';
/** 1 — Bosh slayder */
export const heroSliderBlock = blockCommonSchema.extend({
    type: z.literal('hero-slider'),
    data: z.object({
        slides: z
            .array(z.object({
            _id: z.string().min(1).max(40),
            image: mediaRefSchema.nullable().default(null),
            kicker: translatableString({ max: 120 }),
            title: translatableString({ max: 300 }),
            body: translatableString({ max: 600 }),
            cta: defaulted(blockCtaSchema),
            /**
             * Ikkinchi tugma — maketda hero'da ikkita harakat bor
             * (`C1 Bosh sahifa.dc.html`, HERO): aksentli asosiy va
             * ochiq chegarali ikkilamchi. Matni yoki manzili bo'sh
             * bo'lsa chizilmaydi — eski slaydlar shu sababli
             * o'zgarishsiz ishlaydi.
             */
            cta2: defaulted(blockCtaSchema),
            textAlign: z.enum(['left', 'center']).default('left'),
            isVisible: z.boolean().default(true),
            order: z.number().int().min(0).default(0),
        }))
            .max(12)
            .default([]),
        autoplay: z.boolean().default(true),
        intervalMs: z.number().int().min(2000).max(20000).default(6000),
        height: z.enum(['md', 'lg', 'full']).default('lg'),
    }),
});
/** 2 — Sahifa banneri */
export const pageHeaderBlock = blockCommonSchema.extend({
    type: z.literal('page-header'),
    data: z.object({
        image: mediaRefSchema.nullable().default(null),
        /**
         * Sarlavha USTIDAGI kichik yozuv — maketda «Davlat unitar
         * korxonasi» (`C7`, MARKAZ HAQIDA). Bo'sh bo'lsa chizilmaydi.
         */
        kicker: translatableString({ max: 120 }),
        title: translatableString({ max: 300 }),
        subtitle: translatableString({ max: 600 }),
        showBreadcrumbs: z.boolean().default(true),
    }),
});
/** 3 — Matn (WYSIWYG) */
export const richTextBlock = blockCommonSchema.extend({
    type: z.literal('rich-text'),
    data: z.object({
        content: translatableString(),
        columns: z.union([z.literal(1), z.literal(2)]).default(1),
    }),
});
/** 4 — Matn + rasm */
export const textImageBlock = blockCommonSchema.extend({
    type: z.literal('text-image'),
    data: z.object({
        content: translatableString(),
        image: mediaRefSchema.nullable().default(null),
        imagePosition: z.enum(['left', 'right']).default('right'),
        imageWidth: z.union([z.literal(33), z.literal(50)]).default(50),
        cta: defaulted(blockCtaSchema),
    }),
});
/** 5 — Raqamlardagi ko'rsatkichlar */
export const statsBlock = blockCommonSchema.extend({
    type: z.literal('stats'),
    data: z.object({
        title: translatableString({ max: 200 }),
        items: z
            .array(z.object({
            value: z.string().trim().max(30),
            prefix: z.string().trim().max(10).default(''),
            suffix: z.string().trim().max(10).default(''),
            label: translatableString({ max: 200 }),
            icon: z.string().trim().max(40).default(''),
        }))
            .max(8)
            .default([]),
        animate: z.boolean().default(true),
    }),
});
/** 6 — Ustunliklar */
export const featuresBlock = blockCommonSchema.extend({
    type: z.literal('features'),
    data: z.object({
        title: translatableString({ max: 200 }),
        items: z
            .array(z.object({
            icon: z.string().trim().max(40).default(''),
            /**
             * 600 — kartochka sarlavhasi uchun ko'p, lekin bitta ustunli
             * ro'yxatda (maket `C7`, «faoliyat yo'nalishlari») band butun
             * bir gapdan iborat bo'ladi va 200 belgiga sig'masdi.
             */
            title: translatableString({ max: 600 }),
            text: translatableString({ max: 1000 }),
        }))
            .max(12)
            .default([]),
        /**
         * `1` — maketdagi «faoliyat yo'nalishlari» ro'yxati (`C7`):
         * bitta ustunda, tartib raqami mono shriftda chapda, matn
         * yonida. Kataksiz — bandlar faqat chiziq bilan ajratiladi.
         */
        columns: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).default(3),
    }),
});
/** 7 — Foto galereya */
export const galleryBlock = blockCommonSchema.extend({
    type: z.literal('gallery'),
    data: z.object({
        title: translatableString({ max: 200 }),
        images: z.array(mediaRefSchema).max(EMBEDDED_LIMITS.GALLERY_IMAGES).default([]),
        columns: z.union([z.literal(2), z.literal(3), z.literal(4)]).default(3),
        enableLightbox: z.boolean().default(true),
    }),
});
/** 8 — Video */
export const videoBlock = blockCommonSchema.extend({
    type: z.literal('video'),
    data: z.object({
        title: translatableString({ max: 200 }),
        source: z.enum(['youtube', 'upload']).default('youtube'),
        youtubeUrl: z.string().trim().max(300).default(''),
        videoFile: mediaRefSchema.nullable().default(null),
        poster: mediaRefSchema.nullable().default(null),
    }),
});
/** 9 — Savol-javob */
export const faqBlock = blockCommonSchema.extend({
    type: z.literal('faq'),
    data: z.object({
        title: translatableString({ max: 200 }),
        items: z
            .array(z.object({
            question: translatableString({ max: 400 }),
            answer: translatableString({ max: 4000 }),
        }))
            .max(40)
            .default([]),
    }),
});
/** 10 — Akkordeon */
export const accordionBlock = blockCommonSchema.extend({
    type: z.literal('accordion'),
    data: z.object({
        title: translatableString({ max: 200 }),
        items: z
            .array(z.object({
            title: translatableString({ max: 400 }),
            content: translatableString(),
        }))
            .max(40)
            .default([]),
    }),
});
/** 11 — Xronologiya */
export const timelineBlock = blockCommonSchema.extend({
    type: z.literal('timeline'),
    data: z.object({
        title: translatableString({ max: 200 }),
        items: z
            .array(z.object({
            year: z.string().trim().max(20),
            title: translatableString({ max: 300 }),
            text: translatableString({ max: 2000 }),
            image: mediaRefSchema.nullable().default(null),
        }))
            .max(40)
            .default([]),
    }),
});
/** 12 — Hamkorlar */
export const partnersBlock = blockCommonSchema.extend({
    type: z.literal('partners'),
    data: z.object({
        title: translatableString({ max: 200 }),
        logos: z
            .array(z.object({
            image: mediaRefSchema.nullable().default(null),
            name: z.string().trim().max(120).default(''),
            url: z.string().trim().max(300).default(''),
        }))
            .max(40)
            .default([]),
        isCarousel: z.boolean().default(true),
        grayscale: z.boolean().default(true),
    }),
});
/** 13 — Chaqiriq banneri */
export const ctaBlock = blockCommonSchema.extend({
    type: z.literal('cta'),
    data: z.object({
        title: translatableString({ max: 300 }),
        text: translatableString({ max: 800 }),
        cta: defaulted(blockCtaSchema),
        style: z.enum(['primary', 'dark', 'accent']).default('primary'),
    }),
});
/** 14 — Yandex xarita */
export const mapBlock = blockCommonSchema.extend({
    type: z.literal('map'),
    data: z.object({
        lat: z.number().min(-90).max(90).nullable().default(null),
        lng: z.number().min(-180).max(180).nullable().default(null),
        zoom: z.number().int().min(1).max(20).default(16),
        height: z.number().int().min(200).max(900).default(420),
        markerTitle: translatableString({ max: 200 }),
        showRouteButton: z.boolean().default(true),
    }),
});
/**
 * 15 — Xom HTML. FAQAT `superadmin` uchun (CLAUDE.md §6, TZ 4.2).
 * Ruxsat tekshiruvi Service qatlamida — sxema uni bilmaydi.
 */
export const htmlBlock = blockCommonSchema.extend({
    type: z.literal('html'),
    data: z.object({
        code: z.string().max(50_000).default(''),
    }),
});
//# sourceMappingURL=content.js.map